import _ from '@lodash';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	InputAdornment,
	MenuItem,
	Switch,
	TextField,
	Tooltip,
	Typography
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { ThemeProvider } from '@material-ui/core/styles';
import { selectMainTheme } from 'app/store/fuse/settingsSlice';

const NUMBER_ATTRIBUTE_TYPES = ['Variable', 'SpreadSheet'];
const HEADER_CELL_CLASS = 'px-12 first:pl-24 last:pr-24 bg-gray-100 first:rounded-l-xl last:rounded-r-xl font-medium';
const getRowCellClass = index => (
	`px-6 first:pl-12 last:pr-12 first:rounded-l-xl last:rounded-r-xl ${(index + 1) % 2 === 0 ? 'bg-gray-100' : ''}`
);

const getValueKind = (attribute, value) => {
	if (_.isObject(value)) return 'structured';
	if (_.isBoolean(value)) return 'boolean';
	if (_.isNumber(value) || NUMBER_ATTRIBUTE_TYPES.includes(attribute.type)) return 'number';
	return 'text';
};

const getDraftValue = (value, valueKind) => {
	if (valueKind === 'structured') return JSON.stringify(value);
	if (valueKind === 'boolean') return String(value);
	return value == null ? '' : value;
};

const getAttributeGroupKey = (attribute, index) => (
	`${attribute.entity || 'aspect'}|${attribute.io || 'INPUT'}|${attribute.name}|${index}`
);

const createValueRows = (attribute, attributeIndex, value, valuePath = []) => {
	if (_.isPlainObject(value) && Object.keys(value).length > 0) {
		return Object.keys(value).reduce((result, valueKey) => [
			...result,
			...createValueRows(attribute, attributeIndex, value[valueKey], [...valuePath, valueKey])
		], []);
	}

	const groupKey = getAttributeGroupKey(attribute, attributeIndex);
	const valueKind = getValueKind(attribute, value);

	return [{
		key: `${groupKey}|${valuePath.join('.') || 'value'}`,
		groupKey,
		attribute,
		displayName: [attribute.name, ...valuePath].join('.'),
		valuePath,
		originalValue: value,
		enabled: true,
		valueKind,
		draftValue: getDraftValue(value, valueKind)
	}];
};

const createRows = attributes => (attributes || []).reduce((result, attribute, index) => [
	...result,
	...createValueRows(attribute, index, attribute.value)
], []);

const buildSelectedAttributes = rows => {
	const result = [];
	const resultIndexByGroup = {};

	rows.filter(row => row.enabled).forEach(row => {
		let resultIndex = resultIndexByGroup[row.groupKey];

		if (_.isUndefined(resultIndex)) {
			resultIndex = result.length;
			resultIndexByGroup[row.groupKey] = resultIndex;
			result.push({
				...row.attribute,
				value: row.valuePath.length > 0 ? {} : getSavedValue(row)
			});
		}

		if (row.valuePath.length > 0) {
			_.set(result[resultIndex].value, row.valuePath, getSavedValue(row));
		}
	});

	return result;
};

const isInvalidValue = row => (
	row.enabled
	&& row.valueKind === 'number'
	&& row.draftValue !== ''
	&& !Number.isFinite(Number(row.draftValue))
);

function getSavedValue(row) {
	if (row.valueKind === 'structured') return row.originalValue;
	if (row.valueKind === 'boolean') return row.draftValue === 'true';
	if (row.valueKind === 'number') return row.draftValue === '' ? null : Number(row.draftValue);
	return row.draftValue;
}

function ScenarioValueField({ row, onChange }) {
	if (row.valueKind === 'boolean') {
		return (
			<TextField
				select
				className="my-8"
				variant="outlined"
				style={{ width: 200, maxWidth: '100%' }}
				value={row.draftValue}
				onChange={event => onChange(event.target.value)}
				disabled={!row.enabled}
			>
				<MenuItem value="true">True</MenuItem>
				<MenuItem value="false">False</MenuItem>
			</TextField>
		);
	}

	const structured = row.valueKind === 'structured';
	const invalid = isInvalidValue(row);
	const field = (
		<TextField
			className="my-8"
			variant="outlined"
			style={{ width: 200, maxWidth: '100%' }}
			type={row.valueKind === 'number' ? 'number' : 'text'}
			value={row.draftValue}
			onChange={event => onChange(event.target.value)}
			disabled={!row.enabled}
			error={invalid}
			helperText={invalid ? 'Enter a valid number' : undefined}
			InputProps={{ readOnly: structured }}
			inputProps={{
				min: row.valueKind === 'number' && _.isNumber(row.attribute.min_value) ? row.attribute.min_value : undefined,
				max: row.valueKind === 'number' && _.isNumber(row.attribute.max_value) ? row.attribute.max_value : undefined
			}}
		/>
	);

	if (!structured) return field;

	return (
		<Tooltip title="Structured values cannot be edited in the scenario save dialog">
			<span className="block">{field}</span>
		</Tooltip>
	);
}

export default function WhatIfScenarioSaveDialog(props) {
	const { attributes, defaultName, onCancel, onSave, saving = false } = props;
	const mainTheme = useSelector(selectMainTheme);
	const [name, setName] = useState(defaultName || '');
	const [nameTouched, setNameTouched] = useState(false);
	const [rows, setRows] = useState(() => createRows(attributes));
	const [search, setSearch] = useState('');
	const [showOnlySelected, setShowOnlySelected] = useState(false);

	const selectedCount = rows.filter(row => row.enabled).length;
	const hasInvalidValues = rows.some(isInvalidValue);
	const nameIsValid = name.trim().length > 0;
	const canSave = nameIsValid && selectedCount > 0 && !hasInvalidValues && !saving;

	const visibleRows = useMemo(() => {
		const searchValue = search.trim().toLowerCase();

		return rows.filter(row => {
			if (showOnlySelected && !row.enabled) return false;
			if (!searchValue) return true;

			const searchableValue = [
				row.displayName,
				row.attribute.entity,
				row.attribute.type
			].filter(Boolean).join(' ').toLowerCase();

			return searchableValue.includes(searchValue);
		});
	}, [rows, search, showOnlySelected]);

	const updateRow = (key, change) => {
		setRows(currentRows => currentRows.map(row => (
			row.key === key ? { ...row, ...change } : row
		)));
	};

	const setAllRowsEnabled = enabled => {
		setRows(currentRows => currentRows.map(row => ({ ...row, enabled })));
	};

	const handleSave = () => {
		setNameTouched(true);
		if (!canSave) return;

		onSave({
			name: name.trim(),
			attributes: buildSelectedAttributes(rows)
		});
	};

	return (
		<ThemeProvider theme={mainTheme}>
			<Dialog open fullWidth maxWidth="md">
				<DialogTitle disableTypography className="border-b flex flex-col px-0">
					<div className="flex items-center justify-between gap-32 px-32">
						<div className="whitespace-nowrap">Save Scenario</div>
					</div>
				</DialogTitle>

				<DialogContent className="w-full flex flex-col p-20 overflow-hidden" style={{ height: 'calc(100vh - 220px)', minHeight: 420, maxHeight: 680 }}>
					<div className="flex flex-col sm:flex-row gap-16">
						<TextField
							className="flex-1"
							fullWidth
							required
							autoFocus
							label="Scenario Name"
							variant="outlined"
							value={name}
							onChange={event => setName(event.target.value)}
							onBlur={() => setNameTouched(true)}
							error={nameTouched && !nameIsValid}
							helperText={nameTouched && !nameIsValid ? 'Scenario name is required' : ' '}
							disabled={saving}
						/>
						<TextField
							className="flex-1"
							fullWidth
							variant="outlined"
							label="Search Attributes"
							value={search}
							onChange={event => setSearch(event.target.value)}
							helperText=" "
							disabled={saving}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<SearchIcon fontSize="small" />
									</InputAdornment>
								)
							}}
						/>
					</div>

					<div className="flex flex-wrap items-center gap-8 mb-16">
						<div className="flex items-center gap-4">
							<Button
								size="small"
								variant="outlined"
								onClick={() => setAllRowsEnabled(true)}
								disabled={saving || selectedCount === rows.length}
							>
								Select all
							</Button>
							<Button
								size="small"
								variant="outlined"
								onClick={() => setAllRowsEnabled(false)}
								disabled={saving || selectedCount === 0}
							>
								Deselect all
							</Button>
						</div>
						<div className="flex-1" />
						<Chip
							color={selectedCount > 0 ? 'primary' : 'default'}
							label={`${selectedCount} of ${rows.length} selected`}
							size="small"
						/>
						<FormControlLabel
							className="m-0"
							control={(
								<Switch
									checked={showOnlySelected}
									onChange={event => setShowOnlySelected(event.target.checked)}
									color="primary"
									disabled={saving}
								/>
							)}
							label="Show only selected"
						/>
					</div>

					<div className="flex-1 min-h-0 overflow-auto -mr-20 pr-20">
						<table className="w-full px-16 h-px" aria-label="Scenario attributes">
							<thead>
								<tr className="sticky top-0 z-10 h-48 text-left">
									<th className={HEADER_CELL_CLASS} style={{ width: 72 }}>Save</th>
									<th className={HEADER_CELL_CLASS}>Attribute</th>
									<th className={HEADER_CELL_CLASS} style={{ width: 240 }}>Value</th>
								</tr>
							</thead>
							<tbody>
								{visibleRows.map((row, index) => (
									<tr key={row.key} className="px-32">
										<td className={getRowCellClass(index)}>
											<Checkbox
												checked={row.enabled}
												onChange={event => updateRow(row.key, { enabled: event.target.checked })}
												color="primary"
												disabled={saving}
												inputProps={{ 'aria-label': `Save ${row.displayName}` }}
											/>
										</td>
										<td className={getRowCellClass(index)}>
											<TextField
												className="my-8"
												fullWidth
												variant="outlined"
												value={row.displayName}
												InputProps={{ readOnly: true }}
											/>
										</td>
										<td className={getRowCellClass(index)}>
											<ScenarioValueField
												row={row}
												onChange={draftValue => updateRow(row.key, { draftValue })}
											/>
										</td>
									</tr>
								))}

								{visibleRows.length === 0 && (
									<tr>
										<td colSpan={3} className="h-72 text-center">
											No matching attributes
										</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{selectedCount === 0 && (
						<Typography color="error" className="mt-12">
							Select at least one attribute to save the scenario.
						</Typography>
					)}
				</DialogContent>

				<DialogActions className="justify-between border-t p-20">
					<Button onClick={onCancel} disabled={saving}>
						Cancel
					</Button>
					<Button color="primary" variant="contained" onClick={handleSave} disabled={!canSave}>
						{saving ? 'Saving...' : 'Save'}
					</Button>
				</DialogActions>
			</Dialog>
		</ThemeProvider>
	);
}
